import { trackError, trackUserEvent } from '@vend/utilities'
import { useCallback, useEffect, useState } from 'react'
import { ToastNotifications } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import { IPromise } from 'angular'
import uuid from '@vend/vend-uuid'
import { hasContactInfo } from '../react/modals/common/customer-contact-utils'
import { useAppDispatch, useAppSelector } from '../../state/hooks'
import {
  selectCurrentSale,
  selectCustomer,
  selectLineItems,
  updateLineItems,
} from '../../state/slices/currentSale/currentSaleSlice'
import { containsGiftCard } from '../../functions/lineItem'
import { LineItemUser } from '../../models/line-item'
import {
  focusGlobalSearch,
  useIsCalculatingSale,
  usePosStateManager,
  useRetailerSettingsManager,
  useRegisterManager,
  useSaleManager,
  useTrainingModeManager,
  useUserSwitchingService,
} from '../../ng-compat/hooks'
import { $state } from '../../ng-compat/imports'
import { STATES } from '../../../../lib/routes'
import {
  finalisingSale,
  finalisingSaleComplete,
} from '../../state/slices/uiStateSlice'
import { clientApiEventBus } from '../../managers/client-api-event-bus'
import { WEB_REGISTER } from '../web-register.constant'
import { formatToClientApi } from '../../functions/sale-transformer'
import {
  hasLineItems,
  hasPayments,
  isValidCustomer,
} from '../../functions/sale'
import { print } from '../print/print'
import {
  selectActiveOutletId,
  selectActiveRegisterId,
  selectLoggedInUserId,
} from '../../state/slices/posState/posStateSlice'
import { getFeatureFromCache } from '../../util/feature-cache'
import { FEATURE_ENABLE_INVOICING_QUOTE_SERVICE } from '../../constants/wr-features'
import {
  buildQuotePayload,
  createInvoicingQuote,
} from '../../functions/invoicing/create-invoicing-quote'
import { useSaleModals } from './useSaleModals'

export type MoreActionsItemName =
  | 'QUOTE'
  | 'SERVICE'
  | 'DISCARD'
  | 'UNFULFILLED'
  | 'BULK_SOLD_BY'

export const useMoreSaleActions = () => {
  const t = useT()
  const dispatch = useAppDispatch()
  const retailerSettingsManager = useRetailerSettingsManager()
  const registerManager = useRegisterManager()
  const calculating = useIsCalculatingSale()
  const saleManager = useSaleManager()
  const posStateManager = usePosStateManager()
  const trainingModeManager = useTrainingModeManager()
  const userSwitchingService = useUserSwitchingService()
  const saleCustomer = useAppSelector(selectCustomer)
  const sale = useAppSelector(selectCurrentSale)
  const lineItems = useAppSelector(selectLineItems)
  const registerId = useAppSelector(selectActiveRegisterId)
  const outletId = useAppSelector(selectActiveOutletId)
  const userId = useAppSelector(selectLoggedInUserId)
  const {
    openQuoteCustomerModal,
    openServiceModal,
    openMarkUnfulfilledModal,
  } = useSaleModals()
  const [checkQuoteCanComplete, setCheckQuoteCanComplete] = useState(false)
  const [isCreatingInvoicingQuote, setIsCreatingInvoicingQuote] = useState(
    false
  )

  const isInvoicingQuoteEnabled = getFeatureFromCache(
    FEATURE_ENABLE_INVOICING_QUOTE_SERVICE
  )

  useEffect(() => {
    if (checkQuoteCanComplete) {
      setCheckQuoteCanComplete(false)
      if (hasContactInfo(saleCustomer)) {
        saleManager.quoteSale().then(() => {
          $state.go(STATES.SALE_CONFIRMATION)
        })
      }
    }
  }, [checkQuoteCanComplete, saleCustomer, saleManager])

  const createInvoicingQuoteFromSale = useCallback(async () => {
    if (isCreatingInvoicingQuote) {
      return
    }

    const retailer = retailerSettingsManager.get()

    setIsCreatingInvoicingQuote(true)
    try {
      const payload = buildQuotePayload({
        retailerId: retailer?.id,
        discountProductId: retailer?.discount_product_id, // Use the retailer's configured discount product so the sale can be created.
        outletId,
        registerId,
        userId,
        lineItems,
        customer: saleCustomer,
        saleNote: sale.note,
        printNoteOnReceipt:
          registerManager.getActiveRegister()?.print_note_on_receipt ?? true,
        discount: sale.discount,
        displayDiscount: sale.displayDiscount,
        discountIsPercentage: sale.discountIsPercentage,
        currency: retailer?.currency?.code,
        companyName: retailer?.name,
        subtotal: sale.finals.subtotal,
        totalTax: sale.finals.tax,
        taxes: sale.finals.taxes,
        total: sale.finals.inclusivePrice,
      })

      const result = await createInvoicingQuote(
        payload,
        uuid(),
        `x_series:${retailer?.id || ''}`
      )

      if (result.success && result.quoteId) {
        await saleManager.clearSale()
        window.location.assign(`/quote-builder/quotes/${result.quoteId}`)
      } else {
        ToastNotifications.negative(
          t('Failed to create quote. Please try again.')
        )
      }
    } catch (err: any) {
      trackError(
        'Invoicing Quote Creation Error',
        err?.message || 'Unexpected error creating quote',
        { source: 'useMoreSaleActions', retailer: retailer?.id || '' }
      )
      ToastNotifications.negative(
        t('Failed to create quote. Please try again.')
      )
    } finally {
      setIsCreatingInvoicingQuote(false)
    }
  }, [
    isCreatingInvoicingQuote,
    retailerSettingsManager,
    outletId,
    registerId,
    userId,
    lineItems,
    saleCustomer,
    sale,
    registerManager,
    saleManager,
    t,
  ])

  const quoteSale = useCallback(async () => {
    trackUserEvent('Quotes', 'Clicked: Create Quote')
    if (containsGiftCard(lineItems)) {
      ToastNotifications.negative(
        t(
          `Quotes can’t include gift cards. Remove the gift card and try again.`
        )
      )
      return
    }

    if (isInvoicingQuoteEnabled) {
      await createInvoicingQuoteFromSale()
      return
    }

    if (hasContactInfo(saleCustomer)) {
      await saleManager.quoteSale()
      $state.go(STATES.SALE_CONFIRMATION)
    } else {
      const onClose = async (complete: IPromise<any> | undefined) => {
        if (!complete) {
          return
        }

        // This is an angularjs $q promise so we can't await it.
        // TODO: Once Sale component is rolled out, QuoteCustomerModal can
        // be converted to use a native Promise.
        complete
          .then(async () => {
            // Have to check if we have a user attached on the next render
            setCheckQuoteCanComplete(true)
          })
          .catch(() => {
            return
          })
      }
      openQuoteCustomerModal(onClose)
    }
  }, [
    lineItems,
    openQuoteCustomerModal,
    saleCustomer,
    saleManager,
    t,
    isInvoicingQuoteEnabled,
    createInvoicingQuoteFromSale,
  ])

  const createService = useCallback(async () => {
    posStateManager.endSsboTourIfActive()
    trackUserEvent(
      'Services',
      'sale.service_sale.new_sale.create_service.click'
    )

    openServiceModal()
  }, [openServiceModal, posStateManager])

  const discardSale = useCallback(async () => {
    if (calculating) {
      return
    }

    dispatch(finalisingSale())

    trackUserEvent(WEB_REGISTER.APPLICATION_NAME, 'Sale Action Buttons', {
      saleState: sale.displayStatus,
      button: 'Discard Sale',
    })

    clientApiEventBus.emitEvent({
      eventName: clientApiEventBus.EVENTS.SALE_DISCARD,
      data: {
        sale: formatToClientApi(
          sale,
          lineItems,
          retailerSettingsManager.get().discount_product_id
        ),
      },
    })

    const isNewSale = !sale.id

    clientApiEventBus.emitEvent({
      eventName: clientApiEventBus.EVENTS.SALE_DISCARD,
      data: formatToClientApi(
        sale,
        lineItems,
        retailerSettingsManager.get().discount_product_id
      ),
    })
    if (isNewSale && !hasLineItems(sale)) {
      await saleManager.clearSale()
    } else if (isNewSale && !hasPayments(sale)) {
      await saleManager.clearSale()
    } else {
      if (hasPayments(sale)) {
        const trackingEvent = 'Discard Sale and Print Receipt'
        if (trainingModeManager.trainingModeOnboardingState) {
          trackUserEvent(
            WEB_REGISTER.APPLICATION_NAME,
            trainingModeManager.currentTourType || '',
            { step: trackingEvent }
          )
        } else {
          trackUserEvent(WEB_REGISTER.APPLICATION_NAME, trackingEvent)
        }

        const data = await saleManager.getReceiptData()
        await print({ type: 'receipt-settings-and-template', data })
      }
      await saleManager.voidSale()
    }
    await userSwitchingService.switchUserIfNeeded()
    focusGlobalSearch()
    dispatch(finalisingSaleComplete())
  }, [
    calculating,
    dispatch,
    lineItems,
    retailerSettingsManager,
    sale,
    saleManager,
    trainingModeManager.currentTourType,
    trainingModeManager.trainingModeOnboardingState,
    userSwitchingService,
  ])

  const markUnfulfilled = useCallback(() => {
    if (containsGiftCard(lineItems)) {
      ToastNotifications.negative(
        t(
          `Gift cards can't be marked for fulfillment. Remove the gift card, then try again.`
        )
      )
      return
    }
    posStateManager.endSsboTourIfActive()
    trackUserEvent(
      WEB_REGISTER.APPLICATION_NAME,
      'Opened Mark Sale as Unfulfilled Modal',
      {
        saleId: sale.id,
        customerAttached: isValidCustomer(saleCustomer),
        openedFrom: 'moreActionsDropdown',
      }
    )

    openMarkUnfulfilledModal()
  }, [
    lineItems,
    openMarkUnfulfilledModal,
    posStateManager,
    sale.id,
    saleCustomer,
    t,
  ])

  const bulkChangeSoldBy = useCallback(
    (user?: LineItemUser) => {
      if (!user?.id) {
        return
      }

      const editableLineItems = lineItems.filter(
        lineItem => !lineItem.confirmed
      )
      if (!editableLineItems.length) {
        return
      }

      dispatch(
        updateLineItems(
          editableLineItems.map(lineItem => ({
            ...lineItem,
            salesperson: user,
          }))
        )
      )
      saleManager.saveSale()
    },
    [dispatch, lineItems, saleManager]
  )

  const handleActions = useCallback(
    (item: MoreActionsItemName, user?: LineItemUser) => {
      switch (item) {
        case 'QUOTE':
          return quoteSale()
        case 'SERVICE':
          return createService()
        case 'DISCARD':
          discardSale()
          break
        case 'UNFULFILLED':
          markUnfulfilled()
          break
        case 'BULK_SOLD_BY':
          bulkChangeSoldBy(user)
          break
      }
    },
    [bulkChangeSoldBy, createService, discardSale, markUnfulfilled, quoteSale]
  )

  return handleActions
}
